import { Button } from "@shared-ui/button";
import { useEffect, useRef } from "react";
import { Dialog } from "@/components/ui/dialog";

/**
 * Confirmation for a reconciliation trigger, on the app's own dialog shell
 * rather than `window.confirm`.
 *
 * `window.confirm` blocks the tab, cannot be styled, and sits badly beside the
 * sheets on this desk that trap focus and close on Escape — it is the one
 * control here an operator cannot read with the rest of the screen.
 *
 * The shell (`@/components/ui/dialog`) owns the focus trap, Escape and focus
 * restore. This adds the copy and the two buttons, and starts focus on CONFIRM:
 * `Dialog` focuses the first control, which is Cancel, and the button an
 * operator came to press is the second. Same reasoning as `BulkConfirmDialog`.
 */
export function ReconcileConfirmDialog({
	open,
	title,
	body,
	confirmLabel,
	onConfirm,
	onCancel,
}: {
	open: boolean;
	title: string;
	/** What the run will and will not do, in one sentence. */
	body: string;
	confirmLabel: string;
	onConfirm: () => void;
	onCancel: () => void;
}) {
	const confirmRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (open) confirmRef.current?.focus();
	}, [open]);

	return (
		<Dialog open={open} titleId="reconcile-confirm-title" onClose={onCancel}>
			<h2 id="reconcile-confirm-title" className="text-lg font-semibold">
				{title}
			</h2>
			<p className="text-sm text-slate-700">{body}</p>
			<div className="flex justify-end gap-2 pt-1">
				<Button variant="ghost" onClick={onCancel}>
					Cancel
				</Button>
				<Button ref={confirmRef} onClick={onConfirm}>
					{confirmLabel}
				</Button>
			</div>
		</Dialog>
	);
}
